import React, { useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Copy, Share2, Check, X, Heart, Lock, PartyPopper, Pencil } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { fmtNaira, fmtDate, publicUrl, whatsappLink } from "@/lib/format";
import { THANKS_STYLES, friendlyError, copyText } from "@/lib/askThemes";
import AskCard from "@/components/ask/AskCard";
import LendOwnerPanel from "@/components/ask/LendOwnerPanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const err = (e) => { const x = apiError(e); toast.error(friendlyError(x)); };

function shareText(a, url) {
  if (a.kind === "lend") return `Hi${a.recipient_name ? ` ${a.recipient_name}` : ""} 🙏 I sent you a Lend Me request on Debii. Have a look: ${url}`;
  if (a.style === "simple" || a.style === "custom") return `${a.title || "A little help could make my day"} 💚 ${url}`;
  return `Your guy needs small assistance 😭 ${a.title ? `“${a.title}” ` : ""}${url}`;
}

export function SharePanel({ a, url }) {
  const copy = async () => { if (await copyText(url)) toast.success("Link copied"); else toast.error("Couldn't copy. Long-press the link to copy it."); };
  const native = async () => { try { await navigator.share({ title: "Debii Ask", text: shareText(a, ""), url }); } catch { /* cancelled */ } };
  return (
    <div data-testid="ask-share-panel" className="rounded-2xl border border-border bg-card p-4">
      <p className="text-sm font-semibold">Share your Ask</p>
      <div className="mt-3 grid grid-cols-3 gap-2">
        <a data-testid="ask-share-whatsapp" href={whatsappLink(null, shareText(a, url))} target="_blank" rel="noopener noreferrer"
          className="h-11 rounded-xl bg-[#25D366] text-white text-xs font-bold flex items-center justify-center gap-1.5 active:scale-95 transition-transform">WhatsApp</a>
        <button data-testid="ask-share-copy" onClick={copy} className="h-11 rounded-xl bg-secondary text-xs font-bold flex items-center justify-center gap-1.5 active:scale-95 transition-transform"><Copy className="h-3.5 w-3.5" /> Copy</button>
        <button data-testid="ask-share-more" onClick={native} className="h-11 rounded-xl bg-secondary text-xs font-bold flex items-center justify-center gap-1.5 active:scale-95 transition-transform"><Share2 className="h-3.5 w-3.5" /> More</button>
      </div>
      <p className="mt-2 text-[11px] text-muted-foreground">Tip: post it on your WhatsApp Status. Anyone with the link can see your bank details.</p>
    </div>
  );
}

function ContributionRow({ c, a, onChange }) {
  const [busy, setBusy] = useState(false);
  const [thanksOpen, setThanksOpen] = useState(false);
  const act = async (path) => {
    setBusy(true);
    try { onChange((await api.post(`/asks/${a.id}/contributions/${c.id}/${path}`)).data); } catch (e) { err(e); } finally { setBusy(false); }
  };
  const tone = { reported: "bg-amber-100 text-amber-800", confirmed: "bg-emerald-100 text-emerald-800", rejected: "bg-red-100 text-red-700" }[c.status];
  return (
    <div data-testid={`contribution-${c.id}`} className="rounded-2xl border border-border bg-card p-3.5">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="font-display text-lg font-extrabold">{fmtNaira(c.amount, a.currency)}</p>
          <p className="truncate text-xs text-muted-foreground">{c.name || "Someone"} · {fmtDate(c.created_at)}{c.note ? ` · ${c.note}` : ""}</p>
        </div>
        <span data-testid={`contribution-status-${c.id}`} className={`rounded-full px-2.5 py-1 text-[11px] font-bold ${tone}`}>
          {c.status === "reported" ? "Says sent" : c.status === "confirmed" ? "Received" : "Not received"}
        </span>
      </div>
      {c.status === "reported" && (
        <div className="mt-3 flex gap-2">
          <Button data-testid={`confirm-contribution-button-${c.id}`} disabled={busy} onClick={() => act("confirm")} className="h-10 flex-1 rounded-xl"><Check className="mr-1 h-4 w-4" />Confirm received</Button>
          <Button data-testid={`reject-contribution-button-${c.id}`} disabled={busy} variant="outline" onClick={() => act("reject")} className="h-10 flex-1 rounded-xl"><X className="mr-1 h-4 w-4" />Not received</Button>
        </div>
      )}
      {c.status === "reported" && <p className="mt-2 text-[11px] text-muted-foreground">Check your bank app first — Debii can't see transfers.</p>}
      {c.status === "confirmed" && (
        <button data-testid={`send-thanks-button-${c.id}`} onClick={() => setThanksOpen(true)} className="mt-3 w-full h-10 rounded-xl bg-pink-50 text-pink-700 text-sm font-semibold flex items-center justify-center gap-1.5">
          <Heart className="h-4 w-4" /> {c.thanks_path ? "Share your Thanks again" : "Send Thanks"}
        </button>
      )}
      <ThanksDrawer open={thanksOpen} onOpenChange={setThanksOpen} a={a} c={c} onDone={onChange} />
    </div>
  );
}

function ThanksDrawer({ open, onOpenChange, a, c, onDone }) {
  const [style, setStyle] = useState(c.thanks_style || "playful");
  const [path, setPath] = useState(c.thanks_path);
  const make = async () => {
    try {
      const { data } = await api.post(`/asks/${a.id}/contributions/${c.id}/thanks`, { style });
      setPath(data.path);
      onDone((await api.get(`/asks/${a.id}`)).data);
    } catch (e) { err(e); }
  };
  const token = path?.split("/t/")[1];
  const url = token ? publicUrl(`/api/s/${token}`) : "";
  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent data-testid="thanks-drawer" className="mx-auto max-w-[30rem] px-5 pb-8">
        <DrawerHeader className="px-0"><DrawerTitle className="font-display text-xl">Send a Debii Thanks</DrawerTitle></DrawerHeader>
        <div className="grid grid-cols-3 gap-2">
          {THANKS_STYLES.map((s) => (
            <button key={s.id} data-testid={`thanks-style-option-${s.id}`} onClick={() => setStyle(s.id)}
              className={`rounded-2xl border-2 p-3 text-center transition-all ${style === s.id ? "border-accent bg-emerald-50 scale-[1.03]" : "border-border"}`}>
              <span className="text-3xl">{s.emoji}</span><p className="mt-1 text-sm font-bold">{s.name}</p><p className="text-[10px] leading-tight text-muted-foreground">{s.hint}</p>
            </button>
          ))}
        </div>
        <p className="mt-3 text-[11px] text-muted-foreground">No amounts or bank details are shown on Thanks pages.</p>
        {path && style === c.thanks_style ? (
          <div className="mt-4 grid grid-cols-2 gap-2">
            <a data-testid="thanks-share-whatsapp" href={whatsappLink(null, `Thank you so much 💚 I made you something: ${url}`)} target="_blank" rel="noopener noreferrer" className="h-12 rounded-xl bg-[#25D366] text-white text-sm font-bold flex items-center justify-center">WhatsApp</a>
            <a data-testid="thanks-preview-link" href={path} target="_blank" rel="noopener noreferrer" className="h-12 rounded-xl bg-secondary text-sm font-bold flex items-center justify-center">Preview</a>
          </div>
        ) : (
          <Button data-testid="thanks-generate" onClick={make} className="mt-4 h-12 w-full rounded-xl">Create Thanks link</Button>
        )}
      </DrawerContent>
    </Drawer>
  );
}

function EditDrawer({ open, onOpenChange, a, onDone }) {
  const [f, setF] = useState({ title: a.title || "", message: a.message || "", target: a.target || "", show_progress: a.show_progress });
  const save = async () => {
    try {
      const body = { title: f.title, message: f.message };
      if (a.mode === "open") Object.assign(body, f.target ? { target: Number(f.target) } : { clear_target: true });
      onDone((await api.patch(`/asks/${a.id}`, body)).data);
      onOpenChange(false);
      toast.success("Ask updated");
    } catch (e) { err(e); }
  };
  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent data-testid="ask-edit-drawer" className="mx-auto max-w-[30rem] px-5 pb-8 space-y-3">
        <DrawerHeader className="px-0"><DrawerTitle className="font-display text-xl">Edit Ask</DrawerTitle></DrawerHeader>
        <Input data-testid="ask-edit-title" value={f.title} maxLength={80} onChange={(e) => setF({ ...f, title: e.target.value })} className="h-12 rounded-xl" />
        <Textarea data-testid="ask-edit-message" value={f.message} maxLength={500} onChange={(e) => setF({ ...f, message: e.target.value })} className="rounded-xl" />
        {a.mode === "open" && <Input data-testid="ask-edit-target" inputMode="numeric" placeholder="Target (blank = any amount)" value={f.target} onChange={(e) => setF({ ...f, target: e.target.value.replace(/\D/g, "") })} className="h-12 rounded-xl" />}
        <Button data-testid="ask-edit-save" onClick={save} className="h-12 w-full rounded-xl">Save</Button>
      </DrawerContent>
    </Drawer>
  );
}

export default function AskDetail() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [confirm, setConfirm] = useState(null);
  const [editing, setEditing] = useState(false);
  const { data: a } = useQuery({ queryKey: ["ask", id], queryFn: async () => (await api.get(`/asks/${id}`)).data });
  const update = (fresh) => { qc.setQueryData(["ask", id], fresh); qc.invalidateQueries({ queryKey: ["asks"] }); };
  if (!a) return <div className="debii-shell min-h-[100dvh]" />;

  const url = publicUrl(a.share_path);
  const open = ["active", "pending", "countered", "accepted"].includes(a.status);
  const pending = (a.contributions || []).filter((c) => c.status === "reported");
  const rest = (a.contributions || []).filter((c) => c.status !== "reported");
  const doAction = async (kind) => {
    try { update((await api.post(`/asks/${id}/${kind}`)).data); toast.success(kind === "close" ? "Ask closed" : "Link turned off"); } catch (e) { err(e); }
    setConfirm(null);
  };

  return (
    <div data-testid="ask-detail" className="debii-shell min-h-[100dvh] px-5 pt-5 pb-16 space-y-4">
      <div className="flex items-center justify-between">
        <button data-testid="ask-detail-back" onClick={() => navigate("/asks")} className="h-10 w-10 rounded-xl bg-secondary flex items-center justify-center"><ArrowLeft className="h-5 w-5" /></button>
        <span data-testid="ask-status" className="rounded-full bg-secondary px-3 py-1 text-xs font-bold capitalize">{a.status === "recorded" ? "Recorded" : a.status}</span>
      </div>
      {params.get("new") && <p className="text-center font-display text-lg font-bold ask-pop">Your Ask is ready 🎉</p>}
      <AskCard ask={a} />
      <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
        <span>{a.kind === "lend" ? "Lend Me" : a.mode === "open" ? "Open Ask" : "Personal Ask"}</span>
        {a.has_access_code && <span className="inline-flex items-center gap-1"><Lock className="h-3 w-3" /> Code protected</span>}
        {a.kind === "gift" && <span>{a.confirmed_count} confirmed · {a.pending_count} waiting</span>}
      </div>

      {a.kind === "gift" && a.target_reached_at && a.status === "active" && (
        <div data-testid="ask-target-reached" className="rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 p-4 text-white ask-pop">
          <p className="flex items-center gap-2 font-display text-lg font-extrabold"><PartyPopper className="h-5 w-5" /> Target reached!</p>
          <p className="mt-1 text-sm text-white/85">Close the Ask so no one sends more than needed.</p>
          <Button data-testid="ask-close-after-target" onClick={() => doAction("close")} className="mt-3 h-10 rounded-xl bg-white text-emerald-800 hover:bg-white/90">Close Ask</Button>
        </div>
      )}

      {open && <SharePanel a={a} url={url} />}
      {a.kind === "lend" && <LendOwnerPanel a={a} onChange={update} />}

      {a.kind === "gift" && (
        <div className="space-y-2.5">
          {pending.length > 0 && <h2 className="font-display text-base font-bold">Waiting for you to confirm</h2>}
          {pending.map((c) => <ContributionRow key={c.id} c={c} a={a} onChange={update} />)}
          {rest.length > 0 && <h2 className="pt-2 font-display text-base font-bold">History</h2>}
          {rest.map((c) => <ContributionRow key={c.id} c={c} a={a} onChange={update} />)}
          {!a.contributions?.length && <p data-testid="contributions-empty" className="rounded-2xl border border-dashed border-border p-5 text-center text-sm text-muted-foreground">No contributions reported yet.</p>}
        </div>
      )}

      {open && (
        <div className="flex gap-2 pt-2">
          {a.kind === "gift" && <Button data-testid="ask-edit-button" variant="outline" onClick={() => setEditing(true)} className="h-11 flex-1 rounded-xl"><Pencil className="mr-1 h-4 w-4" />Edit</Button>}
          {a.kind === "gift" && <Button data-testid="ask-close-button" variant="outline" onClick={() => setConfirm("close")} className="h-11 flex-1 rounded-xl">Close</Button>}
          <Button data-testid="ask-revoke-button" variant="outline" onClick={() => setConfirm("revoke")} className="h-11 flex-1 rounded-xl text-red-600">Turn off link</Button>
        </div>
      )}
      {a.kind === "gift" && <EditDrawer key={`${a.title}-${a.target}`} open={editing} onOpenChange={setEditing} a={a} onDone={update} />}

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm === "close" ? "Close this Ask?" : "Turn off this link?"}</AlertDialogTitle>
            <AlertDialogDescription>{confirm === "close" ? "People won't be able to report new contributions. You can still confirm pending ones." : "The link will stop working for everyone. This can't be undone."}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="ask-confirm-action" onClick={() => doAction(confirm)} className="rounded-xl">{confirm === "close" ? "Close Ask" : "Turn off"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
